import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Text } from './text';
import { useUser } from './UserContext';
import { EnvelopeIcon } from '@heroicons/react/24/outline';

const TEMPLATE_LABELS = {
  'solicitud-expediente': 'Solicitud de documentos',
  'pendiente-solicitud': 'Solicitud de pendientes',
  'fianza-emitida': 'Fianza emitida',
  'welcome-client': 'Bienvenida al portal',
  'acceso-cliente': 'Nuevo acceso',
};

function labelFor(row) {
  if (row.templateKey === 'solicitud-expediente' && row.kind === 'recordatorio') {
    return 'Recordatorio de documentos';
  }
  return TEMPLATE_LABELS[row.templateKey] || row.templateKey || 'Correo';
}

function formatSentAt(sentAt) {
  const d = sentAt?.toDate?.();
  if (!d) return '';
  return d.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Per-cliente email history (Correos tab in ClienteDetails). Reads email_log
 * rows stamped with this cliente's id; rules require filtering by orgId.
 */
export function ClienteEmailHistory({ clientId }) {
  const { userData } = useUser();
  const [rows, setRows] = useState(null); // null while loading
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clientId || !userData?.org) return;
    let cancelled = false;
    setRows(null);
    setError(null);
    (async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'email_log'),
          where('orgId', '==', userData.org),
          where('clientId', '==', clientId),
        ));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (b.sentAt?.toMillis?.() || 0) - (a.sentAt?.toMillis?.() || 0));
        if (!cancelled) setRows(list);
      } catch (e) {
        console.error('Failed to load email history:', e);
        if (!cancelled) setError('No se pudo cargar el historial de correos.');
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, userData?.org]);

  if (error) {
    return <Text className="text-sm text-red-700 py-4">{error}</Text>;
  }
  if (rows === null) {
    return (
      <div className="flex justify-center py-10">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-azul-surelia"></div>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-zinc-400">
        <EnvelopeIcon className="w-8 h-8" />
        <Text className="text-sm text-zinc-500">No se han enviado correos de este cliente.</Text>
      </div>
    );
  }

  return (
    <div className="divide-y divide-azul-surelia/10">
      {rows.map((row) => {
        const failed = row.status !== 'sent';
        return (
          <div key={row.id} className="py-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Text className="text-sm font-medium text-zinc-800">{labelFor(row)}</Text>
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${
                failed
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : 'bg-green-50 text-green-700 border-green-200'
              }`}>
                {failed ? 'Falló' : 'Enviado'}
              </span>
            </div>
            <Text className="text-xs text-zinc-600 break-words mt-0.5">
              {row.toName ? `${row.toName} — ${row.to}` : row.to}
            </Text>
            {row.subject && (
              <Text className="text-xs text-zinc-400 break-words">{row.subject}</Text>
            )}
            <Text className="text-[11px] text-zinc-400 mt-0.5">
              {formatSentAt(row.sentAt)}
            </Text>
          </div>
        );
      })}
    </div>
  );
}

export default ClienteEmailHistory;
